// <video-frame>: clears the centred circular loader once the wrapped media can
// paint. Handles native <video> (loadeddata) and embeds (iframe load), with an
// error path and a hard timeout so the spinner can never persist forever.
customElements.define("video-frame", class extends HTMLElement {
  connectedCallback() {
    const media = this.querySelector("video, iframe");
    if (!media) { this.ready(); return; }

    this._timer = setTimeout(() => this.ready(), 6000); // safety cap

    if (media.tagName === "VIDEO") {
      if (media.autoplay) this.kickstart(media);
      if (media.readyState >= 2) { this.ready(); return; } // HAVE_CURRENT_DATA already
      media.addEventListener("loadeddata", () => this.ready(), { once: true });
    } else {
      media.addEventListener("load", () => this.ready(), { once: true });
    }
    media.addEventListener("error", () => this.fail(), { once: true });
  }

  // `autoplay` is a request, not an instruction: the spec only starts playback once the
  // browser believes it can play THROUGH without stalling. On a phone connection that
  // belief never arrives, so a hero that has already buffered its first frames can sit
  // paused forever — measured on throttled 3G, readyState hit 3 and canplay fired at
  // 3.6s, yet the video was still paused at currentTime 0 twelve seconds later. An
  // explicit play() as soon as there IS frame data starts it regardless of that
  // prediction. Everything below is a retry of that one call, for the cases where the
  // first attempt is refused rather than deferred (backgrounded tab, Low Power Mode).
  kickstart(video) {
    const attempt = () => {
      if (video.paused === false || !video.isConnected) return;
      const p = video.play();
      if (p && p.catch) p.catch(() => {}); // refused; a later trigger retries
    };
    video.addEventListener("loadeddata", attempt);
    video.addEventListener("canplay", attempt);
    if (video.readyState >= 2) attempt();

    // A tab restored from the background, and user interaction, are the two moments a
    // previously-refused play() reliably succeeds. Some Android browsers — Samsung
    // Internet exposes an autoplay control with a Wi-Fi-only option — refuse muted
    // autoplay outright on mobile data, and no amount of markup overrides a user's
    // browser setting; the gesture retry is the only route back. These deliberately
    // do NOT use `once`:
    // the first tap usually lands while the video still has no data, so a
    // single-shot listener would spend itself on the one attempt that cannot work.
    // They unbind on `playing` instead, when they have actually done their job.
    this._gestures = ["pointerdown", "touchstart", "keydown", "click"];
    this._attempt = attempt;
    this._onVisible = () => { if (!document.hidden) attempt(); };
    document.addEventListener("visibilitychange", this._onVisible);
    this._gestures.forEach((e) => document.addEventListener(e, attempt, { passive: true }));
    video.addEventListener("playing", () => this.unbind(), { once: true });
  }

  unbind() {
    if (this._gestures && this._attempt) {
      this._gestures.forEach((e) => document.removeEventListener(e, this._attempt));
      this._gestures = null;
    }
  }

  disconnectedCallback() {
    clearTimeout(this._timer);
    if (this._onVisible) document.removeEventListener("visibilitychange", this._onVisible);
    this.unbind();
  }
  ready() { clearTimeout(this._timer); this.classList.add("is-ready"); }
  fail() { clearTimeout(this._timer); this.classList.add("is-error"); }
});
